import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Map, MapPin, FileText, BarChart2, Wrench, Shield, Calendar, LogOut, Layers, Lightbulb, Users, Key } from 'lucide-react';
import { useAuth } from '../../../shared/context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import ChangePasswordModal from '../ChangePasswordModal';

const Home = () => {
  const { logout } = useAuth();
  const { hasPermission, loading: permissionsLoading, userLevel, getPermittedModules } = usePermissions();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const modules = [
    {
      id: 'obras',
      name: 'Sistema de Obras em Vias Públicas',
      description: 'Gerenciamento completo de obras em vias públicas, incluindo cadastro, medições e visualização em mapa.',
      icon: <Map size={28} />,
      color: '#667eea',
      path: '/obras',
      active: true,
      features: ['Cadastro de obras lineares', 'Medições e acompanhamento', 'Visualização em mapa', 'Relatórios gerenciais']
    },
    {
      id: 'obras-fixas',
      name: 'Sistema de Obras Fixas',
      description: 'Gerenciamento de obras pontuais como praças, edifícios públicos e outros projetos fixos.',
      icon: <MapPin size={28} />,
      color: '#9c27b0',
      path: '/obras-fixas',
      active: true,
      features: ['Cadastro de obras pontuais', 'Medições e acompanhamento', 'Visualização em mapa', 'Relatórios gerenciais']
    },
    {
      id: 'mapa-integrado',
      name: 'Mapa Integrado',
      description: 'Visualização integrada de todas as obras lineares e fixas em um único mapa interativo.',
      icon: <Layers size={28} />,
      color: '#3f51b5',
      path: '/mapa-integrado',
      active: true,
      features: ['Visualização de obras lineares', 'Visualização de obras fixas', 'Filtros por tipo e status', 'Detalhes interativos']
    },
    {
      id: 'fossas-septicas',
      name: 'Administração de Fossas Sépticas',
      description: 'Gerenciamento e mapeamento de fossas sépticas em propriedades rurais.',
      icon: <Shield size={28} />,
      color: '#2e7d32',
      path: '/fossas-septicas/dashboard',
      active: true,
      features: ['Cadastro de propriedades', 'Registro de visitas', 'Mapeamento de fossas', 'Dashboard de estatísticas']
    },
    {
      id: 'postes-iluminacao',
      name: 'Postes de Iluminação Pública',
      description: 'Administração e monitoramento dos postes de iluminação pública do município.',
      icon: <Lightbulb size={28} />,
      color: '#ff6f00',
      path: '/postes-iluminacao/dashboard',
      active: true,
      features: ['Cadastro de postes', 'Controle de status', 'Histórico de manutenção', 'Dashboard de estatísticas']
    },
    {
      id: 'controle-demandas',
      name: 'Controle de Demandas',
      description: 'Gerenciamento de demandas vindas do aplicativo móvel e bot do WhatsApp da população.',
      icon: <FileText size={28} />,
      color: '#e74c3c',
      path: '/controle-demandas',
      active: true,
      features: ['Demandas do App e WhatsApp', 'Encaminhamento por setores', 'Análise de soluções', 'Histórico completo']
    },
    {
      id: 'gestao-usuarios',
      name: 'Gestão de Usuários',
      description: 'Administração completa de usuários do sistema, permissões e controle de acesso.',
      icon: <Users size={28} />,
      color: '#6c757d',
      path: '/gestao-usuarios/dashboard',
      active: true,
      features: ['Cadastro de usuários', 'Controle de permissões', 'Níveis de acesso', 'Gerenciamento de senhas']
    }
  ];

  return (
    <div className="home-container">
      <div className="home-header">
        <div className="header-content" style={{ position: 'relative' }}>
          {/* Título e subtítulo centralizados */}
          <div style={{ textAlign: 'center' }}>
            <h1>Sistema Integrado de Gestão Municipal</h1>
            <p>Prefeitura Teste</p>
          </div>

          {/* Botões de ação posicionados absolutamente no canto direito */}
          <div style={{
            position: 'absolute',
            right: '15px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            gap: '8px'
          }}>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="button"
              style={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.25)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                fontSize: '13px'
              }}
              title="Alterar minha senha"
            >
              <Key size={16} />
              Alterar Senha
            </button>
            
            <button
              onClick={logout}
              className="button"
              style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px'
              }}
            >
              <LogOut size={18} />
              Sair
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="dashboard-container">
          <div className="dashboard-header">
            <div className="dashboard-title">
              <h2>Painel de Sistemas</h2>
              <div className="dashboard-subtitle">Selecione um módulo para acessar suas funcionalidades</div>
            </div>
          </div>

          {permissionsLoading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #6c757d',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '16px'
              }}></div>
              <p style={{ color: '#666' }}>Carregando módulos disponíveis...</p>
            </div>
          ) : (
            <>
              {/* Informações do usuário */}
              <div style={{
                marginBottom: '30px',
                padding: '16px 20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Users size={20} color="#6c757d" />
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    Nível de acesso: <strong style={{ color: '#333' }}>{userLevel || 'Não definido'}</strong>
                  </span>
                  <span style={{ fontSize: '14px', color: '#666', marginLeft: '20px' }}>
                    Módulos disponíveis: <strong style={{ color: '#333' }}>{getPermittedModules().length}</strong>
                  </span>
                </div>
              </div>

              <div className="modules-grid">
                {modules
                  .filter(module => hasPermission(module.id))
                  .map(module => (
                    <div
                      key={module.id}
                      className={`module-card ${!module.active ? 'module-disabled' : ''}`}
                    >
                      <div className="module-accent" style={{ backgroundColor: module.color }}></div>
                      <div className="module-content">
                        <div className="module-header">
                          <div className="module-icon-wrapper" style={{ backgroundColor: `${module.color}15`, color: module.color }}>
                            {module.icon}
                          </div>
                          <h3 className="module-title">{module.name}</h3>
                        </div>

                        <p className="module-description">{module.description}</p>

                        <div className="module-features">
                          <ul>
                            {module.features.map((feature, index) => (
                              <li key={index}>{feature}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="module-footer">
                          {module.active ? (
                            <Link to={module.path} className="module-button" style={{ backgroundColor: module.color }}>
                              Acessar Módulo
                            </Link>
                          ) : (
                            <button className="module-button disabled" disabled>
                              Em desenvolvimento
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Mensagem se não tiver acesso a nenhum módulo */}
              {getPermittedModules().length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '12px',
                  border: '1px solid #ffeaa7',
                  margin: '20px 0'
                }}>
                  <Shield size={48} color="#856404" style={{ marginBottom: '16px' }} />
                  <h3 style={{ color: '#856404', marginBottom: '8px' }}>
                    Nenhum módulo disponível
                  </h3>
                  <p style={{ color: '#856404', marginBottom: '16px' }}>
                    Você não tem permissão para acessar nenhum módulo do sistema.
                  </p>
                  <p style={{ color: '#856404', fontSize: '14px' }}>
                    Entre em contato com o administrador para solicitar acesso.
                  </p>
                </div>
              )}
            </>
          )}

          <style jsx>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>

      <div className="container">
        <div className="home-footer">
          <p>© 2025 Prefeitura Teste. Todos os direitos reservados.</p>
        </div>
      </div>

      {/* Modal de Alterar Senha */}
      <ChangePasswordModal 
        isOpen={showPasswordModal} 
        onClose={() => setShowPasswordModal(false)} 
      />
    </div>
  );
};

export default Home;